/**
 * YjsDebugPanel — floating real-time panel showing all active Y.Doc rooms.
 *
 * Toggle with: Ctrl+Shift+Y  (or window.__yjsDebug.toggle())
 *
 * Shows rooms from both:
 *   - crdtServiceWS  (Notes, canvas, etc. — keyed by room string)
 *   - localFirstSyncService  (Doc, Page, Note, Mail.Draft — keyed by workspaceId_entityType_entityId)
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { crdtServiceWS } from '../../services/crdt/crdtServiceWS';
import { localFirstSyncService } from '../../services/sync/localFirstSyncService';
import { getEntitySystemDebugState } from '../../stores/entitySystemYjsStore';

interface RoomSnapshot {
  room: string;
  source: 'crdt-ws' | 'localfirst';
  wsConnected: boolean;
  wsSynced: boolean;
  idbSynced?: boolean;
  docKeys: string[];
  // Populated on expand
  keyDetails?: Array<{ key: string; type: string; length: number; preview: string }>;
}

function getKeyDetails(room: string, source: 'crdt-ws' | 'localfirst'): RoomSnapshot['keyDetails'] {
  try {
    let doc: any;
    if (source === 'crdt-ws') {
      doc = crdtServiceWS.getYDoc(room);
    } else {
      // localFirstSyncService cacheKey is workspaceId_entityType_entityId
      // We can't directly get the ydoc without the parts, but we can inspect via snapshot
      return [];
    }
    if (!doc) return [];
    const details: RoomSnapshot['keyDetails'] = [];
    doc.share.forEach((val: any, key: string) => {
      const type = val?.constructor?.name ?? 'unknown';
      let length = 0;
      let preview = '';
      try {
        if (type === 'YText') { const s = val.toString(); length = s.length; preview = s.slice(0, 80); }
        else if (type === 'YMap') { length = val.size; preview = JSON.stringify(Array.from(val.keys()).slice(0, 5)); }
        else if (type === 'YArray') { length = val.length; }
        else if (type === 'XmlFragment') { length = val.length; preview = val.toString().slice(0, 80); }
      } catch {}
      details.push({ key, type, length, preview });
    });
    return details;
  } catch {
    return [];
  }
}

function getSnapshot(): RoomSnapshot[] {
  const rows: RoomSnapshot[] = [];

  for (const r of crdtServiceWS.getDebugSnapshot()) {
    rows.push({ ...r, source: 'crdt-ws' });
  }
  for (const r of localFirstSyncService.getDebugSnapshot()) {
    // Strip the workspaceId_ prefix to show a shorter label
    const shortRoom = r.room.replace(/^[^_]+_/, '');
    rows.push({ ...r, room: shortRoom, source: 'localfirst' });
  }

  return rows;
}

const STATUS_DOT: React.CSSProperties = {
  display: 'inline-block', width: 8, height: 8,
  borderRadius: '50%', marginRight: 4, flexShrink: 0,
};

export function YjsDebugPanel() {
  const [visible, setVisible] = useState(false);
  const [rooms, setRooms] = useState<RoomSnapshot[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [keyDetails, setKeyDetails] = useState<Record<string, RoomSnapshot['keyDetails']>>({});
  const [copied, setCopied] = useState(false);
  const [entitySystemState, setEntitySystemState] = useState(getEntitySystemDebugState());
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const refresh = useCallback(() => {
    setRooms(getSnapshot());
    setEntitySystemState(getEntitySystemDebugState());
  }, []);

  useEffect(() => {
    if (visible) {
      refresh();
      intervalRef.current = setInterval(refresh, 500);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [visible, refresh]);

  // Global keyboard toggle: Ctrl+Shift+Y
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'Y') setVisible(v => !v);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Expose to window for console use
  useEffect(() => {
    (window as any).__yjsDebug = {
      toggle: () => setVisible(v => !v),
      snapshot: () => {
        const rooms = getSnapshot();
        const es = getEntitySystemDebugState();
        if (es.dataspaceId) {
          rooms.unshift({
            room: `entity-system:${es.dataspaceId}`,
            source: 'crdt-ws',
            wsConnected: es.wsConnected,
            wsSynced: es.wsSynced,
            idbSynced: es.indexeddbSynced,
            docKeys: ['entities', 'edges'],
          });
        }
        return rooms;
      },
    };
  }, []);

  const toggleExpand = (room: string, source: 'crdt-ws' | 'localfirst') => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(room)) {
        next.delete(room);
      } else {
        next.add(room);
        const details = getKeyDetails(room, source);
        setKeyDetails(kd => ({ ...kd, [room]: details }));
      }
      return next;
    });
  };

  const copyAll = useCallback(() => {
    const snap = getSnapshot().map(r => ({
      room: r.room,
      source: r.source,
      wsConnected: r.wsConnected,
      wsSynced: r.wsSynced,
      ...(r.idbSynced !== undefined ? { idbSynced: r.idbSynced } : {}),
      docKeys: r.docKeys,
    }));
    navigator.clipboard.writeText(JSON.stringify(snap, null, 2)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, []);

  if (!visible) return null;

  const panel: React.CSSProperties = {
    position: 'fixed', bottom: 16, left: 16, zIndex: 99999,
    background: 'rgba(10,12,18,0.96)', color: '#e2e8f0',
    border: '1px solid rgba(99,102,241,0.4)',
    borderRadius: 10, padding: '10px 12px',
    fontFamily: 'ui-monospace, monospace', fontSize: 11,
    width: 460, maxHeight: '70vh', overflowY: 'auto',
    boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
    backdropFilter: 'blur(12px)',
  };

  return (
    <div style={panel}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontWeight: 700, fontSize: 12, color: '#a5b4fc' }}>⚡ Y.js Debug Panel</span>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ color: '#64748b', fontSize: 10 }}>{rooms.length} rooms · Ctrl+Shift+Y</span>
          <button
            onClick={copyAll}
            style={{
              background: copied ? 'rgba(34,197,94,0.2)' : 'rgba(99,102,241,0.15)',
              border: `1px solid ${copied ? 'rgba(34,197,94,0.4)' : 'rgba(99,102,241,0.3)'}`,
              borderRadius: 4, color: copied ? '#86efac' : '#a5b4fc',
              cursor: 'pointer', fontSize: 10, padding: '2px 8px', lineHeight: '16px',
              transition: 'all 0.15s',
            }}
          >{copied ? '✓ copied' : 'copy all'}</button>
          <button
            onClick={() => setVisible(false)}
            style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 14, lineHeight: 1 }}
          >✕</button>
        </div>
      </div>

      {/* Entity System Y.Doc status */}
      {entitySystemState.dataspaceId && (
        <div style={{
          marginBottom: 8, borderRadius: 6, padding: '6px 8px',
          background: 'rgba(99,102,241,0.08)',
          border: '1px solid rgba(99,102,241,0.25)',
        }}>
          <div style={{ fontWeight: 700, color: '#a5b4fc', marginBottom: 4, fontSize: 11 }}>
            🗂 EntitySystem Y.Doc
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 12px', fontSize: 10 }}>
            <span style={{ color: '#64748b' }}>Dataspace</span>
            <span style={{ color: '#e2e8f0' }}>…{entitySystemState.dataspaceId.slice(-8)}</span>
            <span style={{ color: '#64748b' }}>Entities</span>
            <span style={{ color: '#e2e8f0' }}>{entitySystemState.entityCount}</span>
            <span style={{ color: '#64748b' }}>Edges</span>
            <span style={{ color: '#e2e8f0' }}>{entitySystemState.edgeCount}</span>
            <span style={{ color: '#64748b' }}>IndexedDB</span>
            <span style={{ color: entitySystemState.indexeddbSynced ? '#22c55e' : '#f59e0b' }}>
              {entitySystemState.indexeddbSynced ? '✓ synced' : '…loading'}
            </span>
            <span style={{ color: '#64748b' }}>WebSocket</span>
            <span style={{ color: entitySystemState.wsConnected ? '#22c55e' : '#ef4444' }}>
              {entitySystemState.wsConnected ? '✓ connected' : '✗ disconnected'}
            </span>
            <span style={{ color: '#64748b' }}>WS Sync</span>
            <span style={{ color: entitySystemState.wsSynced ? '#22c55e' : '#f59e0b' }}>
              {entitySystemState.wsSynced ? '✓ synced' : '…pending'}
            </span>
            {entitySystemState.lastUpdateAt && (
              <>
                <span style={{ color: '#64748b' }}>Last update</span>
                <span style={{ color: '#94a3b8' }}>
                  {Math.round((Date.now() - entitySystemState.lastUpdateAt) / 1000)}s ago
                </span>
              </>
            )}
          </div>
        </div>
      )}

      {rooms.length === 0 && !entitySystemState.dataspaceId && (
        <div style={{ color: '#475569', padding: '8px 0' }}>No active Y.Doc rooms</div>
      )}

      {rooms.map(r => {
        const isExpanded = expanded.has(r.room);
        const allConnected = r.wsConnected && r.wsSynced;
        const partiallyConnected = r.wsConnected && !r.wsSynced;

        return (
          <div key={r.room + r.source} style={{
            marginBottom: 4, borderRadius: 6,
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.07)',
          }}>
            <div
              onClick={() => toggleExpand(r.room, r.source)}
              style={{ padding: '5px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
            >
              {/* WS status dot */}
              <span style={{
                ...STATUS_DOT,
                background: allConnected ? '#22c55e' : partiallyConnected ? '#f59e0b' : '#ef4444',
              }} />

              {/* Room name */}
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#c4b5fd' }}>
                {r.room}
              </span>

              {/* Source badge */}
              <span style={{
                fontSize: 9, padding: '1px 4px', borderRadius: 3,
                background: r.source === 'crdt-ws' ? 'rgba(99,102,241,0.3)' : 'rgba(20,184,166,0.3)',
                color: r.source === 'crdt-ws' ? '#a5b4fc' : '#5eead4',
              }}>{r.source}</span>

              {/* Flags */}
              <span style={{ color: r.wsConnected ? '#22c55e' : '#ef4444', fontSize: 10 }}>
                {r.wsConnected ? 'WS✓' : 'WS✗'}
              </span>
              <span style={{ color: r.wsSynced ? '#22c55e' : '#f59e0b', fontSize: 10 }}>
                {r.wsSynced ? 'sync✓' : 'sync…'}
              </span>
              {r.idbSynced !== undefined && (
                <span style={{ color: r.idbSynced ? '#22c55e' : '#64748b', fontSize: 10 }}>
                  {r.idbSynced ? 'idb✓' : 'idb…'}
                </span>
              )}

              <span style={{ color: '#475569', fontSize: 10 }}>{isExpanded ? '▲' : '▼'}</span>
            </div>

            {isExpanded && (
              <div style={{ padding: '4px 8px 8px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ color: '#64748b', marginBottom: 4 }}>Y.Doc keys: {r.docKeys.join(', ') || '(none)'}</div>
                {(keyDetails[r.room] ?? []).map(kd => (
                  <div key={kd.key} style={{ marginBottom: 3 }}>
                    <span style={{ color: '#7dd3fc' }}>{kd.key}</span>
                    <span style={{ color: '#64748b' }}> ({kd.type}, len={kd.length})</span>
                    {kd.preview && (
                      <div style={{ color: '#94a3b8', paddingLeft: 12, wordBreak: 'break-all', marginTop: 1 }}>
                        "{kd.preview}{kd.preview.length >= 80 ? '…' : ''}"
                      </div>
                    )}
                  </div>
                ))}
                {(keyDetails[r.room] ?? []).length === 0 && r.source === 'crdt-ws' && (
                  <div style={{ color: '#475569' }}>Click to load key details</div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
